import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Sparkles, Loader2, Upload, Trash2, Image as ImageIcon, FileText, FileSpreadsheet, Download, Clipboard } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ImageDrop({ id, label, file, onChange, disabled, testId }) {
  const isPdf = file && (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'));
  const isCsv = file && (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv'));
  return (
    <div>
      <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">{label}</label>
      <input
        type="file"
        accept="image/*,application/pdf,.pdf,text/csv,.csv"
        id={id}
        data-testid={testId}
        className="hidden"
        disabled={disabled}
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
      {file ? (
        <div className="border border-slate-200 rounded-lg p-2 flex items-center gap-3 bg-slate-50">
          {isCsv ? (
            <div className="w-14 h-14 rounded-md bg-emerald-100 border border-emerald-200 flex flex-col items-center justify-center text-emerald-700">
              <FileSpreadsheet className="w-6 h-6" />
              <span className="text-[9px] font-bold">CSV</span>
            </div>
          ) : isPdf ? (
            <div className="w-14 h-14 rounded-md bg-rose-100 border border-rose-200 flex flex-col items-center justify-center text-rose-700">
              <FileText className="w-6 h-6" />
              <span className="text-[9px] font-bold">PDF</span>
            </div>
          ) : (
            <img
              src={URL.createObjectURL(file)}
              alt="preview"
              className="w-14 h-14 rounded-md object-cover bg-white border border-slate-200"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-700 truncate">{file.name}</div>
            <div className="text-xs text-slate-400">{(file.size / 1024).toFixed(0)} KB</div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onChange(null)} disabled={disabled} className="text-slate-400 hover:text-rose-600">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <label htmlFor={id} className={`block cursor-pointer border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-[#00247D] hover:bg-blue-50/30 transition-colors ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
          <Upload className="w-5 h-5 mx-auto mb-1 text-slate-400" />
          <span className="text-xs text-slate-500">Tap to upload PNG / JPG / PDF / CSV</span>
        </label>
      )}
    </div>
  );
}

function TeamSource({ team, mode, setMode, file, setFile, paste, setPaste, disabled }) {
  const base = team === 'home' ? 'scan-home' : 'scan-away';
  return (
    <div>
      <div className="flex gap-1 mb-2" role="tablist">
        <button
          type="button"
          onClick={() => setMode('file')}
          className={`flex-1 text-[11px] font-bold uppercase tracking-wide py-1.5 rounded-md border transition-colors ${mode === 'file' ? 'bg-[#00247D] text-white border-[#00247D]' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
          disabled={disabled}
          data-testid={`${base}-mode-file`}>
          <Upload className="w-3 h-3 inline mr-1" /> Upload File
        </button>
        <button
          type="button"
          onClick={() => setMode('paste')}
          className={`flex-1 text-[11px] font-bold uppercase tracking-wide py-1.5 rounded-md border transition-colors ${mode === 'paste' ? 'bg-[#00247D] text-white border-[#00247D]' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
          disabled={disabled}
          data-testid={`${base}-mode-paste`}>
          <Clipboard className="w-3 h-3 inline mr-1" /> Paste CSV
        </button>
      </div>
      {mode === 'file' ? (
        <ImageDrop
          id={`${base}-image`}
          testId={`${base}-image-input`}
          label=""
          file={file}
          onChange={setFile}
          disabled={disabled}
        />
      ) : (
        <div>
          <textarea
            value={paste}
            onChange={e => setPaste(e.target.value)}
            disabled={disabled}
            placeholder={"Paste rows from Excel / Google Sheets.\nSeparated by tabs OR commas.\n\nExample (tab-separated):\n#\tPlayer\tPTS\tFGM\tFGA\t3PM\t3PA\tFTM\tFTA\tOR\tDR\tAS\tSTL\tBLK\tTO\tPF\n23\tLeBron Eagle\t28\t10\t18\t3\t7\t5\t6\t2\t6\t7\t1\t1\t3\t2"}
            className="w-full h-32 text-[11px] font-mono border border-slate-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-[#00247D]/30 focus:border-[#00247D] resize-y"
            data-testid={`${base}-paste-input`}
          />
          <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
            <Clipboard className="w-2.5 h-2.5" />
            {paste.trim() ? `${paste.trim().split('\n').length} rows` : 'Paste rows above'}
          </div>
        </div>
      )}
    </div>
  );
}

export function ScanCreateGameDialog({
  open,
  onOpenChange,
  initialName = '',
  initialHomeName = '',
  initialAwayName = '',
  eventId = '',
  matchId = '',
  seasonId = '',
  onCreated,
}) {
  const [gameName, setGameName] = useState(initialName);
  const [homeName, setHomeName] = useState(initialHomeName);
  const [awayName, setAwayName] = useState(initialAwayName);
  const [homeFile, setHomeFile] = useState(null);
  const [awayFile, setAwayFile] = useState(null);
  const [homePaste, setHomePaste] = useState('');
  const [awayPaste, setAwayPaste] = useState('');
  const [homeMode, setHomeMode] = useState('file'); // 'file' | 'paste'
  const [awayMode, setAwayMode] = useState('file');
  const [scanning, setScanning] = useState(false);

  // Sync prop changes when dialog re-opens
  React.useEffect(() => {
    if (open) {
      setGameName(initialName);
      setHomeName(initialHomeName);
      setAwayName(initialAwayName);
      setHomeFile(null);
      setAwayFile(null);
      setHomePaste('');
      setAwayPaste('');
      setHomeMode('file');
      setAwayMode('file');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const downloadCsvTemplate = () => {
    const header = 'name,jersey_number,pts,fgm,fga,3pm,3pa,ftm,fta,oreb,dreb,ast,stl,blk,to,foul';
    const sample = '"Example Player","23",18,7,15,2,5,2,2,1,3,4,1,0,2,3';
    const blob = new Blob([header + '\n' + sample + '\n'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'boxscore_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (!gameName.trim()) { toast.error('Game name is required'); return; }

    // Build the actual File/Blob for home (either uploaded file or pasted CSV text)
    const homePastedText = homePaste.trim();
    const awayPastedText = awayPaste.trim();

    let homePayload = null;
    if (homeMode === 'paste' && homePastedText) {
      homePayload = new Blob([homePastedText], { type: 'text/csv' });
      homePayload.name = 'home_paste.csv';
    } else if (homeMode === 'file' && homeFile) {
      homePayload = homeFile;
    }
    if (!homePayload) { toast.error('Upload or paste the Home box score'); return; }

    let awayPayload = null;
    if (awayMode === 'paste' && awayPastedText) {
      awayPayload = new Blob([awayPastedText], { type: 'text/csv' });
      awayPayload.name = 'away_paste.csv';
    } else if (awayMode === 'file' && awayFile) {
      awayPayload = awayFile;
    }

    // File size preflight (only applies to real File objects)
    const overHome = homePayload instanceof File && homePayload.size > 15 * 1024 * 1024;
    const overAway = awayPayload instanceof File && awayPayload.size > 15 * 1024 * 1024;
    if (overHome || overAway) {
      toast.error('File too large (max 15MB per file). Try shrinking the PDF or photo.');
      return;
    }

    setScanning(true);
    const homeIsPdf = homePayload instanceof File && (homePayload.type === 'application/pdf' || homePayload.name.toLowerCase().endsWith('.pdf'));
    const homeIsCsv = homePastedText || (homePayload instanceof File && (homePayload.type === 'text/csv' || homePayload.name.toLowerCase().endsWith('.csv')));
    let loadingMsg;
    if (homeIsCsv) loadingMsg = 'Parsing CSV and creating your game…';
    else if (homeIsPdf) loadingMsg = 'Reading PDF (may take up to 60 seconds for scanned docs)…';
    else loadingMsg = 'AI is reading both box scores and creating your game…';
    const tId = toast.loading(loadingMsg, { duration: 180000 });
    try {
      const token = localStorage.getItem('session_token');
      const fd = new FormData();
      fd.append('name', gameName.trim());
      if (homeName.trim()) fd.append('home_team_name', homeName.trim());
      if (awayName.trim()) fd.append('away_team_name', awayName.trim());
      if (eventId) fd.append('event_id', eventId);
      if (matchId) fd.append('match_id', matchId);
      if (seasonId) fd.append('season_id', seasonId);
      // Attach payloads with explicit filename so backend content-type detection works
      fd.append('home_image', homePayload, homePayload.name || 'home');
      if (awayPayload) fd.append('away_image', awayPayload, awayPayload.name || 'away');

      const res = await fetch(`${API_URL}/api/stats/games/scan-create`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      let data = null;
      try {
        data = await res.json();
      } catch {
        const text = await res.text().catch(() => '');
        toast.dismiss(tId);
        toast.error(`Server error (HTTP ${res.status})${text ? `: ${text.slice(0, 200)}` : ''}`);
        return;
      }
      toast.dismiss(tId);
      if (!res.ok) {
        toast.error(data.detail || `Scan failed (HTTP ${res.status})`);
        return;
      }
      const homeMsg = `${data.home.team_name}: ${data.home.scanned_players} players, ${data.home.entries_created} stats`;
      const awayMsg = data.away.scanned_players > 0
        ? `${data.away.team_name}: ${data.away.scanned_players} players, ${data.away.entries_created} stats`
        : `${data.away.team_name}: empty roster (manual entry)`;
      const dualNote = data.dual_team_detected ? ' (both teams from one upload!)' : '';
      toast.success(`Game created!${dualNote} ${homeMsg} · ${awayMsg}`);
      onOpenChange(false);
      onCreated && onCreated(data.game_id);
    } catch (err) {
      toast.dismiss(tId);
      console.error('Scan-create error:', err);
      toast.error(err?.message || 'Could not reach the server');
    } finally {
      setScanning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="scan-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#00247D]">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Import Box Score & Create Game
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Upload a photo, PDF, or CSV — or <strong>paste rows directly</strong> from Excel / Google Sheets.
            Photos & PDFs use AI to extract names, jerseys, and every stat. CSV or pasted rows are parsed instantly.
            If your PDF contains <strong>both teams</strong>, one upload is enough — both rosters fill automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">Game Name</label>
            <Input
              placeholder="e.g. SLK Hoods vs Hurricanes"
              value={gameName}
              onChange={e => setGameName(e.target.value)}
              disabled={scanning}
              data-testid="scan-create-name"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Input
                placeholder="Home team name (optional, AI will guess)"
                value={homeName}
                onChange={e => setHomeName(e.target.value)}
                disabled={scanning}
                className="h-9 text-sm"
                data-testid="scan-create-home-name"
              />
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Home Box Score *</label>
              <TeamSource
                team="home"
                mode={homeMode}
                setMode={setHomeMode}
                file={homeFile}
                setFile={setHomeFile}
                paste={homePaste}
                setPaste={setHomePaste}
                disabled={scanning}
              />
            </div>
            <div className="space-y-2">
              <Input
                placeholder="Away team name (optional, AI will guess)"
                value={awayName}
                onChange={e => setAwayName(e.target.value)}
                disabled={scanning}
                className="h-9 text-sm"
                data-testid="scan-create-away-name"
              />
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Away Box Score (skip if Home file has both teams)</label>
              <TeamSource
                team="away"
                mode={awayMode}
                setMode={setAwayMode}
                file={awayFile}
                setFile={setAwayFile}
                paste={awayPaste}
                setPaste={setAwayPaste}
                disabled={scanning}
              />
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-md p-2 text-[11px] text-amber-800 flex items-start gap-2">
            <ImageIcon className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="flex-1 leading-relaxed">
              <div><strong>Photos:</strong> glare-free shot of the row-by-row stats. <strong>PDFs:</strong> text-layer PDFs give near-perfect accuracy; scanned PDFs fall back to AI vision. <strong>CSVs:</strong> use the template format (no AI, instant parse).</div>
            </div>
            <button
              type="button"
              onClick={downloadCsvTemplate}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#00247D] hover:underline shrink-0"
              data-testid="scan-create-csv-template">
              <Download className="w-3 h-3" /> CSV Template
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={scanning}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={scanning || !gameName.trim() || (homeMode === 'file' ? !homeFile : !homePaste.trim())}
            className="bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-black font-semibold"
            data-testid="scan-create-submit">
            {scanning ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Importing…</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-1.5" /> Import & Create Game</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
